// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
import React, {useMemo, useRef, useState, useCallback} from 'react';
import cn from 'classnames';
import store from '../../store.js';
import {bw_to_readable} from '../../utils/misc.js';
import {Text} from '../../comp/text';
import {Icon} from '../../comp/icon';
import {Dropdown} from '../../comp/dropdown';
import './usage_chart.less';

const Lazy_bar = React.lazy(()=>
    import('react-chartjs-2').then(m=>({default: m.Bar})));
const Lazy_line = React.lazy(()=>
    import('react-chartjs-2').then(m=>({default: m.Line})));

const tooltips_label_cb = (tip, d)=>{
    const dataset = d.datasets[tip.datasetIndex];
    const value = dataset.data[tip.index];
    return `Bandwidth: ${bw_to_readable(value)} (${tip.xLabel})`;
};

const tooltips_title_cb = ([tip], d)=>{
    const dataset = d.datasets[tip.datasetIndex];
    return dataset.label;
};

const DEVICE_LIMIT = 500;
const COLORS = {
    ACCENT: '#af87ff',
    PRIMARY: '#7937ff',
};

const OPTIONS = {
    legend: {
        display: false
    },
    tooltips: {mode: 'nearest', callbacks: {
        label: tooltips_label_cb,
        title: tooltips_title_cb,
    }},
    maintainAspectRatio: false,
    responsive: true,
    scales: {
        xAxes: [{
            stacked: true,
            gridLines: {
                color: 'transparent',
                zeroLineColor: 'transparent',
            },
            ticks: {
                fontColor: 'rgba(255, 255, 255, 0.5)',
            },
        }],
        yAxes: [{
            stacked: true,
            gridLines: {
                color: 'rgba(255, 255, 255, 0.5)',
                zeroLineColor: 'rgba(255, 255, 255, 0.5)',
                lineWidth: 0.5,
                zeroLineWidth: 0.5,
                borderDash: [8],
                tickMarkLength: 0,
                drawBorder: false,
            },
            ticks: {
                userCallback: bw_to_readable,
                suggestedMin: 0,
                beginAtZero: true,
                padding: 5,
                fontColor: 'rgba(255, 255, 255, 0.5)',
            },
        }]
    }
};

const ALL_ITEM = {id: 'all', title: 'All devices combined'};
const STEP_ITEMS = [
    {id: 'daily', title: 'Daily'},
    {id: 'weekly', title: 'Weekly'},
    {id: 'monthly', title: 'Monthly'},
];

const get_dataset_style = (filter, index)=>{
    if (filter==ALL_ITEM.id)
    {
        return {
            backgroundColor: index%2 ? COLORS.PRIMARY : COLORS.ACCENT,
            maxBarThickness: 16,
        };
    }
    return {
        fill: false,
        borderColor: COLORS.PRIMARY,
        pointBackgroundColor: COLORS.ACCENT,
        pointRadius: 2,
        borderWidth: 4,
    };
};

export function Usage_chart({className}){
    const [device_filter, set_df] = useState(ALL_ITEM.id);
    const [step, set_step] = useState(STEP_ITEMS[0].id);
    const handle_step_change = useCallback(id=>{
        set_step(id);
        requestAnimationFrame(store.use_usage.invalidate);
    }, []);
    const usage_params = useMemo(()=>({step}), [step]);
    const [list] = store.use_usage(false, usage_params);
    const chart_ref = useRef();
    const data = useMemo(()=>{
        if (!list?.length || list.length > DEVICE_LIMIT)
            return {};
        const res = {
            labels: Object.keys(list[0].data).sort((a, b)=>a>b ? 1 : -1),
            datasets: [],
        };
        for (let i=0; i<list.length; i++)
        {
            const device = list[i];
            if (device_filter!=ALL_ITEM.id && device_filter!=device._id)
                continue;
            res.datasets.push({
                label: device.name||device._id,
                data: res.labels.map(l=>device.data[l]),
                ...get_dataset_style(device_filter, i),
            });
        }

        return res;
    }, [list, device_filter]);
    const too_many = useMemo(()=>list && list.length > DEVICE_LIMIT, [list]);
    const dd_list = useMemo(()=>{
        const res = [ALL_ITEM];
        if (!Array.isArray(list))
            return res;
        for (const device of list)
            res.push({id: device._id, title: device.name||device._id});
        return res;
    }, [list]);
    const Chart_comp = device_filter==ALL_ITEM.id ? Lazy_bar : Lazy_line;
    return <div className={cn('ea_usage_chart', className)}>
      <Text.xxl h2 className="eauc_title">Usage</Text.xxl>
      <div className="eauc_controls">
        <Dropdown value={device_filter} items={dd_list} on_change={set_df}
          size="s" className="eauc_control"/>
        <Dropdown value={step} items={STEP_ITEMS} size="s"
          on_change={handle_step_change} className="eauc_control"/>
      </div>
      <div className="eau_chart">
        <React.Suspense fallback={<Icon className="eau_spinner" ico="spinner"
          size="huge"/>}>
          {too_many
            ? <Text.m>Too many devices, usage chart cannot be drawn</Text.m>
            : <Chart_comp data={data} options={OPTIONS} ref={chart_ref}/>
          }
        </React.Suspense>
      </div>
    </div>;
}
